/* eslint-disable no-undef */
import axios from 'axios';

import { call, put } from 'redux-saga/effects';

import authenticationService from '../../services/authentication';
import userService from '../../services/users';
import storageHelper from '../../helpers/storage';

import { getErrorList } from '../../helpers/responseErrorApi';

function* requestAuthenticate(action) {
  try {
    const response = yield call(authenticationService.authenticate, action.payload.data);

    yield put({ type: Types.validUsernamePassword, payload: { user: { login: action.payload.data.username } } });

    axios.defaults.headers.common.Authorization = 'Bearer ' + response.data.token;

    storageHelper.addValue('token', response.data.token);
    storageHelper.addValue('companyId', null);
    storageHelper.addValue('regionalId', null);
    storageHelper.addValue('officeId', null);

    yield call(requestSelfData, {});

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess();
    }
  } catch (error) {
    console.error(error);

    const errors = getErrorList(error);

    if (Array.isArray(errors) && errors.length > 0) {
      yield put({ type: Types.failedAuthentication, payload: errors[0] });
    }

    action?.payload?.onError(errors);
  }
}

function* requestLogout(action) {
  try {
    yield put({ type: Types.successLogout });

    const storage = storageHelper.get();
    delete storage.token;
    delete storage.officeId;
    delete storage.companyId;
    delete storage.regionalId;

    storageHelper.set(storage);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess();
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

function* requestGetMyOffices(_action) {
  try {
    const response = yield call(userService.getMyOffices);

    yield put({ type: Types.successGetOffices, payload: response.data });
  } catch (error) {
    console.error(error);
  }
}

function* requestSelfData(_action) {
  // Fluxo apenas para cenários onde a empresa e a regional não precisam ser selecionadas
  const storage = storageHelper.get();

  if (storage === null || !storage.token) {
    yield put({
      type: Types.failedAuthentication,
      payload: {
        code: 'TokenNotFound',
        defaultMessage: 'Token not found'
      }
    });
    return;
  }

  try {
    const response = yield call(userService.getData);
    const user = response.data;

    const companyId = storage.companyId || user?.companies?.[0] || '';
    const regionalId = storage.regionalId || user?.regionals?.[0] || '';
    const officeId = storage.officeId || user?.offices?.[0] || '';

    yield put({ type: Types.successSelfData, payload: user });

    yield put({ type: 'SUCCESS_SELECTED_COMPANY', payload: companyId });
    yield put({ type: 'SUCCESS_SELECTED_REGIONAL', payload: regionalId });
    yield put({ type: 'SUCCESS_SELECTED_OFFICE', payload: officeId });

    axios.defaults.headers.common.Authorization = 'Bearer ' + storage.token;
    axios.defaults.headers.common['X-Company-Id'] = companyId;
    axios.defaults.headers.common['X-Regional-Id'] = regionalId;
    axios.defaults.headers.common['X-Office-Id'] = officeId;

    storageHelper.addValue('companyId', companyId);
    storageHelper.addValue('regionalId', regionalId);
    storageHelper.addValue('officeId', officeId);

    yield call(requestGetMyOffices);
  } catch (error) {
    yield put({
      type: Types.failedAuthentication,
      payload: {
        code: 'GetUserDataError',
        defaultMessage: 'Get user data error'
      }
    });
  }
}

function* requestRecoverPassword(action) {
  try {
    yield call(userService.recoverPassword, action.payload.userEmail);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess();
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

function* requestChangePassword(action) {
  try {
    const response = yield call(userService.changePassword, action.payload.data);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess(response);
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

function* requestSelfCreate(action) {
  try {
    const response = yield call(userService.create, action.payload.data);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess(response);
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

function* requestValidateActivateCode(action) {
  try {
    const response = yield call(userService.validateActivateCode, action.payload.code);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess(response);
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

function* requestResendActivateCode(action) {
  try {
    const response = yield call(userService.resendActivateCode, action.payload.code);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess(response);
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

function* requestUpdateProfile(action) {
  try {
    const response = yield call(userService.updateProfile, action.payload.data);

    if (action.payload && action.payload.onSuccess) {
      action.payload.onSuccess(response);
    }
  } catch (error) {
    if (action.payload && action.payload.onError) {
      action.payload.onError(error);
    }
  }
}

function* requestGetCompanies(_action) {
  try {
    const data = yield call(listActiveCompanies);

    yield put({ type: Types.successGetCompanies, payload: data });
  } catch (error) {
    console.error(error);
  }
}

function* requestGetRegionals(action) {
  try {
    const data = yield call(listActiveRegionals, action.payload.companyId);

    yield put({ type: Types.successGetRegionals, payload: data });
  } catch (error) {
    console.error(error);
  }
}

function* requestGetOffices(action) {
  try {
    const data = yield call(listActiveOffices, action.payload.regionalId);

    yield put({ type: Types.successGetOffices, payload: data });
  } catch (error) {
    console.error(error);
  }
}

function* requestSelectCompany(action) {
  yield put({ type: 'SUCCESS_SELECTED_COMPANY', payload: action.payload });
  yield put({ type: 'SUCCESS_SELECTED_REGIONAL', payload: '' });
  yield put({ type: 'SUCCESS_SELECTED_OFFICE', payload: '' });

  axios.defaults.headers.common['X-Company-Id'] = action.payload;
  axios.defaults.headers.common['X-Regional-Id'] = '';
  axios.defaults.headers.common['X-Office-Id'] = '';

  storageHelper.addValue('companyId', action.payload);
  storageHelper.addValue('regionalId', '');
  storageHelper.addValue('officeId', '');

  yield call(requestGetRegionals, { payload: { companyId: action.payload } });
}

function* requestSelectRegional(action) {
  yield put({ type: 'SUCCESS_SELECTED_REGIONAL', payload: action.payload });
  yield put({ type: 'SUCCESS_SELECTED_OFFICE', payload: '' });

  axios.defaults.headers.common['X-Regional-Id'] = action.payload;
  axios.defaults.headers.common['X-Office-Id'] = '';

  storageHelper.addValue('regionalId', action.payload);
  storageHelper.addValue('officeId', '');

  yield call(requestGetOffices, { payload: { regionalId: action.payload } });
}

function* requestSelectOffice(action) {
  yield put({ type: 'SUCCESS_SELECTED_OFFICE', payload: action.payload });

  axios.defaults.headers.common['X-Office-Id'] = action.payload;

  storageHelper.addValue('officeId', action.payload);
}

const Actions = {
  requestAuthenticate,
  requestLogout,
  requestSelfData,
  requestRecoverPassword,
  requestChangePassword,
  requestSelfCreate,
  requestValidateActivateCode,
  requestResendActivateCode,
  requestUpdateProfile,
  requestGetCompanies,
  requestGetRegionals,
  requestGetOffices,
  requestSelectCompany,
  requestSelectRegional,
  requestSelectOffice,
}

const Types = {
  authenticate: 'REQUEST_USER_AUTHENTICATE',
  failedAuthentication: 'FAILED_USERS_AUTHENTICATION',
  logout: 'REQUEST_USER_LOGOUT',
  successLogout: 'SUCCESS_USERS_LOGOUT',
  selfData: 'REQUEST_USER_SELF_DATA',
  successSelfData: 'SUCCESS_USERS_SELF_DATA',
  validUsernamePassword: 'VALID_USERNAME_PASSWORD',
  recoverPassword: 'REQUEST_RECOVER_PASSWORD',
  officeContextUpdated: 'OFFICE_CONTEXT_UPDATED',
  changePassword: 'REQUEST_CHANGE_PASSWORD',
  selfCreate: 'REQUEST_SELF_CREATE',
  validateActivateCode: 'REQUEST_VALIDATE_ACTIVATE_CODE',
  resendActivateCode: 'REQUEST_RESEND_ACTIVATE_CODE',
  updateProfile: 'REQUEST_UPDATE_PROFILE',
  officeContextUpdated: 'OFFICE_CONTEXT_UPDATED',
  requestGetCompanies: 'REQUEST_GET_COMPANIES',
  requestGetRegionals: 'REQUEST_GET_REGIONALS',
  requestGetOffices: 'REQUEST_GET_OFFICES',
  successGetCompanies: 'SUCCESS_GET_COMPANIES',
  successGetRegionals: 'SUCCESS_GET_REGIONALS',
  successGetOffices: 'SUCCESS_GET_OFFICES',
  requestSelectCompany: 'REQUEST_SELECT_COMPANY',
  requestSelectRegional: 'REQUEST_SELECT_REGIONAL',
  requestSelectOffice: 'REQUEST_SELECT_OFFICE',
}

export {
  Actions,
  Types,
}
